import React from 'react'
import clsx from 'clsx'
import { debounce } from 'lodash'
import { Fade, Menu, MenuItem, useTheme } from '@mui/material'

import { ChannelOptionsType } from '..'
import { postUser, searchUser } from '../../../../../lib/api'
import { useAppDispatch } from '../../../../../hooks/redux'
import { AddErrorAlert } from '../../../../../slices/alert'
import { DEBOUNCE_DELAY } from '../../../../../hooks/useDelayedUserSearch'
import { MIN_CHAR_COUNT } from '../../../../../hooks/useDelayedUserSearch/v2'
import { getFormatted } from '../../../../../utilities/phoneNumber'
import { checkE164 } from '../../../../../utilities'
import { useEnvironment } from '../../../../../providers/EnvironmentProvider'

import PhoneFlag from '../../../../atoms/PhoneFlag'
import SmsPill from './Pill/Index'
import PhoneInput from '../../../../atoms/PhoneInput'

import useStyles from './styles'
import Tooltip from '../../../../atoms/Tooltip'
import RemoveAgentIcon from '../../../ChatWindowV2/Header/TransferModal/Icon'

const PHONE_ID = 'new-message-sms-input'
interface SmsInputProps {
  users: Array<any>
  setUsers: (val: any) => void
  type: string
  creatingSms: boolean
  setCreatingSms: (val: boolean) => void
  createUser: (
    value: string,
    name: string,
    method: ChannelOptionsType
  ) => object
}

const SmsInput = (
  {
    users,
    setUsers,
    type,
    createUser,
    creatingSms,
    setCreatingSms
  }: SmsInputProps,
  ref
) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const { getEnvValue } = useEnvironment()
  const [searchVal, setSearchVal] = React.useState('')
  const [value, setVal] = React.useState('')
  const [suggestions, setSuggestions] = React.useState([])
  const [noResults, setNoResults] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [focused, setFocused] = React.useState(false)
  const [newUser, setNewUser] = React.useState('')
  const anchorRef = React.useRef<HTMLDivElement>()
  const classes = useStyles()

  const saveNewUser = React.useCallback(
    async formatted => {
      try {
        const userToSave = createUser(formatted, newUser, 'SMS') as any
        userToSave.type_id =
          getEnvValue('config.users', 'u_default_external_type_id', null) ??
          getEnvValue('config.users', 'users_default_external_type_id', null)
        const user = await postUser(userToSave)
        const channel = user.channels[0]
        //add new user to recipents
        setUsers(old => {
          return [...old, { user, channel }]
        })
        //cleanup
        setCreatingSms(false)
        setNewUser('')
        setVal('')
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch, createUser, getEnvValue, newUser, setUsers, setCreatingSms]
  )

  const checkValidation = React.useCallback(
    val => {
      const smsNumber = val
      if (smsNumber === '' || smsNumber === null) {
        dispatch(
          AddErrorAlert({
            err: { message: `Error creating new contact: Add a channel value` }
          })
        )
        return [true, '']
      }

      const [error, formatted] = checkE164(smsNumber)
      if (error) {
        dispatch(
          AddErrorAlert({
            err: { message: `Error creating new contact: ${error}` }
          })
        )
      }
      return [error, formatted]
    },
    [dispatch]
  )

  const handleKeyDown = React.useCallback(
    async e => {
      if (e.keyCode === 13) {
        const [error, formatted] = checkValidation(value)
        if (newUser === '') {
          dispatch(
            AddErrorAlert({
              err: { message: `Error creating new contact: Add contact name` }
            })
          )
          return
        }
        if (!error) {
          saveNewUser(formatted)
        }
      }
    },
    [value, checkValidation, saveNewUser, dispatch, newUser]
  )

  const handleBlur = React.useCallback(() => {
    const [error, formatted] = checkValidation(value)
    if (newUser === '') {
      dispatch(
        AddErrorAlert({
          err: { message: `Error creating new contact: Add contact name` }
        })
      )
      return
    }
    if (!error) {
      saveNewUser(formatted)
    }
    setFocused(false)
  }, [value, checkValidation, saveNewUser, dispatch, newUser])

  // Fetches and stores users and data
  const handleSearch = React.useCallback(
    async chars => {
      try {
        setNoResults(false)
        setLoading(true)
        const results = await searchUser(chars, [type])
        const values = []
        setNoResults(results.length === 0)
        results.forEach(user => {
          user.channels.forEach(channel => {
            if (channel.type === type && channel.value) {
              values.push({ user, channel })
            }
          })
        })

        const sorted = values.sort((a, b) => {
          const aName = a?.user?.name
          const bName = b?.user?.name
          return aName.localeCompare(bName)
        })
        setLoading(false)
        setSuggestions(sorted)
      } catch (err) {
        setLoading(false)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch, type]
  )

  const handleOnRemoveClick = React.useCallback(
    (user, channel) => {
      setUsers(old => {
        const indexForRemoval = old.findIndex(
          entry => entry.user.id === user.id && entry.channel.id === channel.id
        )
        const copy = [...old]
        const result = copy
          .slice(0, indexForRemoval)
          .concat(copy.slice(indexForRemoval + 1))
        return result
      })
      if (ref.current) {
        setTimeout(() => {
          ref.current.focus()
        }, 0)
      }
    },
    [setUsers, ref]
  )

  //eslint-disable-next-line
  const delayedSearch = React.useCallback(
    debounce(handleSearch, DEBOUNCE_DELAY, {
      leading: true
    }),
    []
  )

  const handleOnSearchChange = React.useCallback(
    e => {
      const val = e.target.value
      if (val.length > MIN_CHAR_COUNT) {
        delayedSearch(val)
      } else {
        setSuggestions([])
      }
      setSearchVal(val)
    },
    [delayedSearch]
  )

  const showResults = React.useMemo(
    () => searchVal.length > MIN_CHAR_COUNT,
    [searchVal.length]
  )

  React.useEffect(() => {
    setMenuOpen(showResults)
    if (showResults) {
      // Refocus input
      if (ref.current) {
        setTimeout(() => {
          ref.current.focus()
        }, 0)
      }
    }
  }, [showResults, ref])

  return (
    <div className={clsx(classes.root, classes.extraPadding)}>
      <div className={classes.sectionRoot} ref={anchorRef}>
        <div className={classes.pillsSection}>
          {users.map(data => {
            const { user, channel } = data
            const smsChannels = user.channels.filter(
              c => c.type === 'sms_voice'
            )
            return (
              <div
                className={classes.pill}
                key={`${user.id}-${channel.id}-selected-sms`}
              >
                <SmsPill
                  user={user}
                  channel={channel}
                  smsChannels={smsChannels}
                  handleOnRemoveClick={handleOnRemoveClick}
                  setUsers={setUsers}
                />
              </div>
            )
          })}

          {creatingSms && (
            <div className={classes.pill}>
              <div>
                <input
                  className={classes.newUserNameInput}
                  value={newUser}
                  onChange={e => {
                    const val = e.target.value
                    setNewUser(val)
                  }}
                />
                <div>
                  <PhoneInput
                    value={value}
                    onChange={val => {
                      setVal(val)
                    }}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder='Enter channel value'
                    onFocus={() => setFocused(true)}
                    borderless
                    autoWidth
                    noMarginLeft
                    opacity={'1'}
                    fontSize={theme.size.sm}
                    inputHeight={'20'}
                    name={PHONE_ID}
                    id={PHONE_ID}
                    isValid={true}
                    inputClass={classes.channelInput}
                  />
                </div>
              </div>{' '}
              {!focused && (
                <Tooltip title='Remove from list' arrow>
                  <span>
                    <RemoveAgentIcon
                      onClick={() => {
                        setNewUser('')
                        setVal('')
                        setCreatingSms(false)
                      }}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          )}

          <input
            className={classes.input}
            value={searchVal}
            onChange={handleOnSearchChange}
            ref={ref}
            placeholder='Type to search or add new contact ...'
          />
        </div>
      </div>

      {menuOpen && (
        <Menu
          open={menuOpen}
          disableEnforceFocus
          anchorEl={anchorRef.current}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'center', vertical: 4 }}
          TransitionComponent={Fade}
          classes={{ paper: classes.resultsContainer }}
          PaperProps={{
            style: {
              width: `${
                anchorRef.current?.getBoundingClientRect()?.width ?? 0
              }px`
            }
          }}
        >
          {loading && (
            <div className={classes.noResults}>Searching contacts...</div>
          )}
          {noResults && (
            <div className={classes.noResults}>No match result found</div>
          )}
          <div
            className={classes.newContact}
            onClick={() => {
              // begin creating new user
              setNewUser(searchVal)
              // Reset state
              setSearchVal('')
              setCreatingSms(true)
              setMenuOpen(false)
              setSuggestions([])
              //select input
              setTimeout(() => {
                const input = document.querySelector<HTMLInputElement>(
                  `#${PHONE_ID}`
                )
                if (input) {
                  input.focus()
                }
              }, 1)
            }}
          >
            + Add
            <strong>{searchVal}</strong>
            to new contact
          </div>
          <div>
            {suggestions.map(result => {
              const { channel, user } = result
              return (
                <MenuItem
                  key={`${user.id}-${channel.id}`}
                  component='div'
                  className={classes.result}
                  onClick={() => {
                    // Sets unique users.
                    setUsers(old => {
                      const alreadyExists = old.some(
                        entry =>
                          entry.user.id === user.id &&
                          entry.channel.id === channel.id
                      )
                      if (alreadyExists) {
                        return old
                      }
                      return [...old, { user, channel }]
                    })
                    // Reset state
                    setSearchVal('')
                    setMenuOpen(false)
                    setSuggestions([])
                  }}
                  disableGutters
                >
                  <div>
                    <label className={classes.smsResult}>{user?.name}</label>
                    <PhoneFlag value={channel.value} className={classes.flag} />
                    <label className={classes.smsValue}>
                      {getFormatted(channel.value)}
                    </label>
                  </div>
                </MenuItem>
              )
            })}
          </div>
        </Menu>
      )}
    </div>
  )
}

export default React.forwardRef(SmsInput)
